/** Éditeur Docs collaboratif — interface façon Google Docs (onglets, barre d'outils compacte). */
import Quill from 'quill';
import QuillCursors from 'quill-cursors';
import { QuillBinding } from 'y-quill';
import { el, clear } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { Drive } from '../api.js';
import { toast } from '../ui/toast.js';
import { contextMenu, promptModal } from '../ui/modal.js';
import { connectDoc, renderPresence, Y } from '../collab.js';
import { shareModal } from './drive.js';
import { toggleTheme, getTheme } from '../ui/theme.js';

const YMap = Y.Map;

// Polices + tailles (taille libre en pt façon Google Docs)
const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'georgia', 'times-new-roman', 'courier-new', 'verdana', 'roboto'];
Quill.register(Font, true);
const Size = Quill.import('attributors/style/size');
Size.whitelist = null; // toute valeur (ex. "11pt") autorisée
Quill.register(Size, true);
Quill.register('modules/cursors', QuillCursors, true);

const SIZE_SCALE = [6, 7, 8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 60, 72, 96];
const ZOOMS = [50, 75, 90, 100, 125, 150, 200];
const rid = () => Math.random().toString(36).slice(2, 10);

const ic = (p) => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`;
const ICONS = {
  undo: '<path d="M9 7H15a5 5 0 0 1 0 10H8"/><path d="M9 3 5 7l4 4"/>',
  redo: '<path d="M15 7H9a5 5 0 0 0 0 10h7"/><path d="m15 3 4 4-4 4"/>',
  print: '<path d="M6 9V3h12v6M6 18H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1h-2M6 14h12v7H6z"/>',
  minus: '<path d="M5 12h14"/>', plus: '<path d="M12 5v14M5 12h14"/>',
  down: '<path d="m6 9 6 6 6-6"/>',
};

const TOOLBAR_HTML = `
  <span class="ql-formats gdoc-grp">
    <button class="gdoc-tb" data-act="undo" title="Annuler">${ic(ICONS.undo)}</button>
    <button class="gdoc-tb" data-act="redo" title="Rétablir">${ic(ICONS.redo)}</button>
    <button class="gdoc-tb" data-act="print" title="Imprimer">${ic(ICONS.print)}</button>
  </span>
  <span class="ql-formats gdoc-grp">
    <button class="gdoc-tb gdoc-zoom" data-act="zoom" title="Zoom"><span class="zlabel">100%</span>${ic(ICONS.down)}</button>
  </span>
  <span class="ql-formats gdoc-grp"><select class="ql-header"><option value="1"></option><option value="2"></option><option value="3"></option><option selected></option></select></span>
  <span class="ql-formats gdoc-grp"><select class="ql-font"><option selected></option><option value="georgia"></option><option value="times-new-roman"></option><option value="courier-new"></option><option value="verdana"></option><option value="roboto"></option></select></span>
  <span class="ql-formats gdoc-grp gdoc-sizegrp">
    <button class="gdoc-tb gdoc-step" data-act="size-dec" title="Réduire">${ic(ICONS.minus)}</button>
    <input class="gdoc-size" value="11" aria-label="Taille" />
    <button class="gdoc-tb gdoc-step" data-act="size-inc" title="Agrandir">${ic(ICONS.plus)}</button>
  </span>
  <span class="ql-formats gdoc-grp">
    <button class="ql-bold"></button><button class="ql-italic"></button><button class="ql-underline"></button><button class="ql-strike"></button>
    <select class="ql-color"></select><select class="ql-background"></select><button class="ql-link"></button>
  </span>
  <span class="ql-formats gdoc-grp">
    <button class="ql-align" value=""></button><button class="ql-align" value="center"></button><button class="ql-align" value="right"></button><button class="ql-align" value="justify"></button>
    <button class="ql-list" value="check"></button><button class="ql-list" value="bullet"></button><button class="ql-list" value="ordered"></button>
    <button class="ql-indent" value="-1"></button><button class="ql-indent" value="+1"></button>
  </span>
  <span class="ql-formats"><button class="ql-clean"></button></span>`;

const MENUS = ['Fichier', 'Édition', 'Affichage', 'Insertion', 'Format', 'Outils', 'Aide'];

export function renderDocs(ctx) {
  const { content, params, user } = ctx;
  const nodeId = params.id;
  let binding, provider, doc, stopPresence, saveTimer, quill;
  let destroyed = false;
  let currentTab = 'quill';

  const root = el('div', { class: 'gdoc' });
  content.style.padding = '0';
  content.appendChild(root);

  (async () => {
    let node;
    try { ({ node } = await Drive.node(nodeId)); }
    catch (e) { root.appendChild(el('div', { class: 'gdoc-error' }, el('h2', {}, 'Document introuvable'), el('p', {}, e.message), el('a', { href: '/drive', class: 'gdoc-share' }, 'Retour au Drive'))); return; }
    if (destroyed) return;
    const readOnly = node.role === 'viewer';
    document.title = node.name + ' — JHN Docs';

    const conn = connectDoc(nodeId, user); doc = conn.doc; provider = conn.provider;

    // ── En-tête ──
    const home = el('a', { class: 'gdoc-home', href: '/drive', title: 'Accueil JHN' }, el('img', { src: '/assets/logo.svg', alt: 'JHN' }));
    const title = el('input', { class: 'gdoc-title', 'aria-label': 'Nom du document' }); title.value = node.name; title.readOnly = readOnly;
    const renameBtn = readOnly ? null : el('button', { class: 'gdoc-iconbtn', title: 'Renommer le document', html: icon('rename', 18) });
    const star = el('button', { class: 'gdoc-iconbtn', title: 'Favori', html: starSvg(node.starred) });
    star.addEventListener('click', async () => { node.starred = !node.starred; star.innerHTML = starSvg(node.starred); try { await Drive.update(nodeId, { starred: node.starred }); } catch {} });
    const saved = el('span', { class: 'gdoc-saved' }, 'Connexion…');
    const menubar = el('div', { class: 'gdoc-menubar' });
    MENUS.forEach((m) => { const b = el('button', { class: 'gdoc-menu' }, m); b.addEventListener('click', () => openMenu(m, b)); menubar.appendChild(b); });
    const presence = el('div', { class: 'presence' });
    const shareBtn = el('button', { class: 'gdoc-share', html: shareSvg() + 'Partager' });
    shareBtn.addEventListener('click', () => shareModal(node));
    const avatar = el('div', { class: 'avatar sm', title: user.email || '' });
    if (user.picture) avatar.appendChild(el('img', { src: user.picture, alt: '' })); else avatar.textContent = ((user.given_name || user.nickname || '?')[0] || 'J').toUpperCase();
    const themeBtn = el('button', { class: 'gdoc-iconbtn', title: 'Thème clair/sombre', html: icon(getTheme() === 'light' ? 'moon' : 'sun', 19) });
    themeBtn.addEventListener('click', () => { const t = toggleTheme(); themeBtn.innerHTML = icon(t === 'light' ? 'moon' : 'sun', 19); });
    const header = el('header', { class: 'gdoc-header' }, home,
      el('div', { class: 'gdoc-headmain' }, el('div', { class: 'gdoc-titlerow' }, title, renameBtn || '', star, saved), menubar),
      el('div', { class: 'gdoc-headright' }, presence, node.role === 'owner' ? shareBtn : '', themeBtn, avatar));

    // ── Barre d'outils ──
    const toolbar = el('div', { id: 'gdoc-toolbar', class: 'gdoc-toolbar ql-toolbar ql-snow', html: TOOLBAR_HTML });
    const toolbarWrap = el('div', { class: 'gdoc-toolbar-wrap' }, toolbar);

    // ── Onglets du document ──
    const tabsList = el('div', { class: 'gdoc-tabs-list' });
    const addTabBtn = el('button', { class: 'gdoc-tab-add', title: 'Ajouter un onglet', html: icon('plus', 18) });
    const tabsPanel = el('aside', { class: 'gdoc-tabs' },
      el('div', { class: 'gdoc-tabs-head' }, el('b', {}, 'Onglets du document'), readOnly ? '' : addTabBtn),
      tabsList);

    // ── Page ──
    const editorHost = el('div', { class: 'gdoc-editor' });
    const page = el('div', { class: 'gdoc-page' }, editorHost);
    const canvas = el('div', { class: 'gdoc-canvas' }, page);
    const body = el('div', { class: 'gdoc-body' }, tabsPanel, canvas);

    root.append(header, toolbarWrap, body);

    // ── Quill ──
    quill = new Quill(editorHost, {
      theme: 'snow', readOnly, placeholder: 'Commencez à écrire…',
      modules: { cursors: { transformOnTextChange: true }, toolbar: readOnly ? false : toolbar, history: { delay: 600, maxStack: 200, userOnly: true } },
    });
    if (readOnly) toolbarWrap.style.display = 'none';

    const ytabs = doc.getArray('tabs');
    const textOf = (id) => (id === 'quill' ? doc.getText('quill') : doc.getText('tab:' + id));
    binding = new QuillBinding(textOf(currentTab), quill, provider.awareness);

    function switchTab(id) {
      if (id === currentTab) return;
      try { binding && binding.destroy(); } catch {}
      currentTab = id;
      binding = new QuillBinding(textOf(id), quill, provider.awareness);
      renderTabs();
      setTimeout(() => quill.focus(), 30);
    }
    function renderTabs() {
      clear(tabsList);
      ytabs.forEach((t, i) => {
        const id = t.get('id'), name = t.get('title') || `Onglet ${i + 1}`;
        const nameEl = el('span', { class: 'gdoc-tab-name', title: 'Double-cliquez pour renommer' }, name);
        const item = el('div', { class: 'gdoc-tab' + (id === currentTab ? ' active' : '') },
          el('span', { class: 'gdoc-tab-ic', html: icon('doc', 16) }), nameEl);
        item.addEventListener('click', () => switchTab(id));
        if (!readOnly) {
          nameEl.addEventListener('dblclick', (e) => { e.preventDefault(); e.stopPropagation(); inlineRename(t, nameEl); });
          const more = el('button', { class: 'gdoc-tab-more', html: icon('more', 16) });
          more.addEventListener('click', (e) => { e.stopPropagation(); tabMenu(t, e); });
          item.appendChild(more);
        }
        tabsList.appendChild(item);
      });
    }
    function inlineRename(t, nameEl) {
      const inp = el('input', { class: 'gdoc-tab-edit', value: t.get('title') || '' });
      nameEl.replaceWith(inp);
      inp.focus(); inp.select();
      let closed = false;
      const done = (save) => { if (closed) return; closed = true; const v = inp.value.trim(); if (save && v) t.set('title', v); renderTabs(); };
      inp.addEventListener('click', (e) => e.stopPropagation());
      inp.addEventListener('keydown', (e) => { e.stopPropagation(); if (e.key === 'Enter') done(true); else if (e.key === 'Escape') done(false); });
      inp.addEventListener('blur', () => done(true));
    }
    function tabMenu(t, e) {
      const r = e.currentTarget.getBoundingClientRect();
      contextMenu(r.left, r.bottom + 4, [
        { label: 'Renommer', icon: 'rename', onClick: async () => { const v = await promptModal({ title: 'Renommer l’onglet', label: 'Nom', value: t.get('title'), confirm: 'Renommer' }); if (v) t.set('title', v); } },
        { sep: true },
        { label: 'Supprimer', icon: 'trash', danger: true, onClick: () => {
          if (ytabs.length <= 1) return toast('Au moins un onglet est requis.', 'err');
          const idx = ytabs.toArray().indexOf(t); const id = t.get('id');
          ytabs.delete(idx, 1);
          if (id === currentTab) switchTab(ytabs.get(0).get('id')); else renderTabs();
        } },
      ]);
    }
    provider.once('synced', () => {
      if (ytabs.length === 0) { const m = newTab('Onglet 1', 'quill'); ytabs.push([m]); }
      currentTab = ytabs.get(0).get('id');
      if (currentTab !== 'quill') { try { binding.destroy(); } catch {} binding = new QuillBinding(textOf(currentTab), quill, provider.awareness); }
      renderTabs();
    });
    ytabs.observeDeep(renderTabs); // observeDeep : capte aussi le renommage d'un onglet (t.set('title'))
    renderTabs();

    function newTab(titleStr, id) { const m = new YMap(); m.set('id', id || rid()); m.set('title', titleStr); return m; }
    if (!readOnly) addTabBtn.onclick = () => { const m = newTab(`Onglet ${ytabs.length + 1}`); ytabs.push([m]); switchTab(m.get('id')); };

    // ── Outils barre : zoom + taille + annuler/rétablir/imprimer ──
    const zlabel = toolbar.querySelector('.zlabel');
    const sizeInput = toolbar.querySelector('.gdoc-size');
    toolbar.querySelectorAll('[data-act]').forEach((b) => b.addEventListener('click', (e) => {
      const a = b.dataset.act;
      if (a === 'undo') quill.history.undo();
      else if (a === 'redo') quill.history.redo();
      else if (a === 'print') window.print();
      else if (a === 'size-dec') stepSize(-1);
      else if (a === 'size-inc') stepSize(1);
      else if (a === 'zoom') {
        const r = b.getBoundingClientRect();
        contextMenu(r.left, r.bottom + 4, ZOOMS.map((z) => ({ label: z + ' %', icon: 'chevron', onClick: () => { page.style.zoom = z / 100; zlabel.textContent = z + '%'; } })));
      }
    }));
    function applySize(pt) { quill.format('size', pt + 'pt'); sizeInput.value = String(pt); quill.focus(); }
    function stepSize(dir) {
      let cur = parseFloat(sizeInput.value) || 11, i = 0;
      while (i < SIZE_SCALE.length - 1 && SIZE_SCALE[i] < cur) i++;
      if (dir > 0 && SIZE_SCALE[i] <= cur) i = Math.min(SIZE_SCALE.length - 1, i + 1);
      if (dir < 0) i = Math.max(0, i - 1);
      applySize(SIZE_SCALE[i]);
    }
    sizeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); const v = parseFloat(sizeInput.value); if (v) applySize(v); } });
    quill.on('editor-change', () => { const f = quill.getFormat(); const s = f.size ? String(f.size).replace('pt', '') : '11'; if (document.activeElement !== sizeInput) sizeInput.value = s; });

    // ── Titre collaboratif ──
    const ytitle = doc.getText('title');
    // On ne touche JAMAIS title.value pendant que l'utilisateur édite (focus) -> frappe fluide.
    const applyTitle = () => { const v = ytitle.toString(); if (document.activeElement !== title && v) title.value = v; document.title = ((v || title.value) || 'Sans titre') + ' — JHN Docs'; };
    provider.once('synced', () => { if (ytitle.length === 0 && node.name) doc.transact(() => ytitle.insert(0, node.name)); applyTitle(); });
    ytitle.observe(applyTitle);
    function commitTitle() {
      const v = (title.value || '').trim() || 'Sans titre';
      if (ytitle.toString() !== v) doc.transact(() => { ytitle.delete(0, ytitle.length); ytitle.insert(0, v); });
      document.title = v + ' — JHN Docs';
      Drive.update(nodeId, { name: v }).catch(() => {});
    }
    if (!readOnly) {
      let tdeb;
      title.addEventListener('input', () => { document.title = (title.value || 'Sans titre') + ' — JHN Docs'; clearTimeout(tdeb); tdeb = setTimeout(commitTitle, 500); });
      title.addEventListener('blur', commitTitle);
      title.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); title.blur(); } });
      renameBtn && renameBtn.addEventListener('click', async () => {
        const v = await promptModal({ title: 'Renommer le document', label: 'Nom', value: title.value, confirm: 'Renommer' });
        if (v) { title.value = v; commitTitle(); }
      });
    }

    // ── Présence + sauvegarde ──
    stopPresence = renderPresence(presence, provider, user.id);
    const setSaved = (txt, busy) => { saved.classList.toggle('busy', !!busy); saved.textContent = txt; };
    provider.on('status', (e) => setSaved(e.status === 'connected' ? 'Enregistré dans JHN' : 'Reconnexion…', e.status !== 'connected'));
    provider.on('sync', (s) => setSaved(s ? 'Enregistré dans JHN' : 'Synchronisation…', !s));
    let t; doc.on('update', () => { setSaved('Enregistrement…', true); clearTimeout(t); t = setTimeout(() => setSaved('Enregistré dans JHN', false), 800); });

    if (!readOnly) setTimeout(() => quill.focus(), 120);

    // ── Menus ──
    function openMenu(name, btn) { const r = btn.getBoundingClientRect(); contextMenu(r.left, r.bottom + 4, menuItems(name)); }
    function fmt(name, val) { const cur = quill.getFormat(); quill.format(name, val === undefined ? !cur[name] : val); }
    function exportPdf() {
      const prev = document.title;
      document.title = (ytitle.toString() || title.value || 'Document').trim();
      setTimeout(() => { window.print(); setTimeout(() => { document.title = prev; }, 300); }, 30);
    }
    function menuItems(name) {
      switch (name) {
        case 'Fichier': return [
          { label: 'Nouveau document', icon: 'doc', onClick: async () => { try { const { node: n } = await Drive.newFile(null, 'doc'); window.open('/doc/' + n.id, '_blank', 'noopener'); } catch (e) { toast(e.message, 'err'); } } },
          { label: 'Nouvel onglet', icon: 'plus', onClick: () => { if (!readOnly) addTabBtn.onclick(); } },
          { label: 'Renommer', icon: 'rename', onClick: async () => { const v = await promptModal({ title: 'Renommer', label: 'Nom', value: title.value, confirm: 'Renommer' }); if (v) { title.value = v; doc.transact(() => { ytitle.delete(0, ytitle.length); ytitle.insert(0, v); }); Drive.update(nodeId, { name: v }).catch(() => {}); } } },
          node.role === 'owner' ? { label: 'Partager', icon: 'share', onClick: () => shareModal(node) } : null,
          { sep: true },
          { label: 'Exporter en PDF', icon: 'doc', onClick: () => exportPdf() },
          { label: 'Imprimer', icon: 'doc', onClick: () => window.print() },
          { label: 'Retour au Drive', icon: 'back', onClick: () => { window.location.href = '/drive'; } },
        ];
        case 'Édition': return [
          { label: 'Annuler', icon: 'back', onClick: () => quill.history.undo() },
          { label: 'Rétablir', icon: 'chevron', onClick: () => quill.history.redo() },
          { sep: true }, { label: 'Tout sélectionner', icon: 'list', onClick: () => quill.setSelection(0, quill.getLength()) },
        ];
        case 'Affichage': return [
          { label: getTheme() === 'light' ? 'Thème sombre' : 'Thème clair', icon: getTheme() === 'light' ? 'moon' : 'sun', onClick: () => toggleTheme() },
          { label: 'Plein écran', icon: 'grid', onClick: () => { if (!document.fullscreenElement) document.documentElement.requestFullscreen?.(); else document.exitFullscreen?.(); } },
        ];
        case 'Insertion': return [
          { label: 'Lien', icon: 'share', onClick: async () => { const u = await promptModal({ title: 'Insérer un lien', label: 'URL', placeholder: 'https://…', confirm: 'Insérer' }); if (u) fmt('link', u); } },
          { label: 'Image (URL)', icon: 'slides', onClick: async () => { const u = await promptModal({ title: 'Insérer une image', label: 'URL', placeholder: 'https://…', confirm: 'Insérer' }); if (u) { const r = quill.getSelection(true); quill.insertEmbed(r.index, 'image', u, 'user'); } } },
          { sep: true },
          { label: 'Liste à puces', icon: 'list', onClick: () => fmt('list', 'bullet') },
          { label: 'Liste numérotée', icon: 'list', onClick: () => fmt('list', 'ordered') },
          { label: 'Case à cocher', icon: 'check', onClick: () => fmt('list', 'check') },
        ];
        case 'Format': return [
          { label: 'Gras', icon: 'doc', onClick: () => fmt('bold') }, { label: 'Italique', icon: 'doc', onClick: () => fmt('italic') },
          { label: 'Souligné', icon: 'doc', onClick: () => fmt('underline') }, { label: 'Barré', icon: 'doc', onClick: () => fmt('strike') },
          { sep: true }, { label: 'Titre 1', icon: 'doc', onClick: () => fmt('header', 1) }, { label: 'Titre 2', icon: 'doc', onClick: () => fmt('header', 2) },
          { label: 'Texte normal', icon: 'doc', onClick: () => fmt('header', false) },
          { sep: true }, { label: 'Effacer la mise en forme', icon: 'x', onClick: () => { const r = quill.getSelection(); if (r) quill.removeFormat(r.index, r.length); } },
        ];
        case 'Outils': return [{ label: 'Nombre de mots', icon: 'recent', onClick: () => { const txt = quill.getText().trim(); const w = txt ? txt.split(/\s+/).length : 0; toast(`${w} mot${w > 1 ? 's' : ''} · ${quill.getLength() - 1} caractères`); } }];
        default: return [{ label: 'JHN Docs — édition collaborative', icon: 'doc' }, { label: 'Onglets, temps réel, sauvegarde auto.', icon: 'recent' }];
      }
    }
  })();

  return () => {
    destroyed = true; content.style.padding = ''; clearTimeout(saveTimer);
    try { stopPresence && stopPresence(); } catch {}
    try { binding && binding.destroy(); } catch {}
    try { provider && provider.destroy(); } catch {}
    try { doc && doc.destroy(); } catch {}
  };
}

function starSvg(on) { return `<svg viewBox="0 0 24 24" width="20" height="20" fill="${on ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"><path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.6 1-5.8-4.3-4.1 5.9-.9L12 3.5Z"/></svg>`; }
function shareSvg() { return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="2.5"/><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="19" r="2.5"/><path d="m8.2 10.7 7.6-4.4M8.2 13.3l7.6 4.4"/></svg>`; }
